import React, { useEffect, useRef, useState } from "react";
import { ListProps, useQuery } from "react-admin";
import {Card, Col, Row, Select, Typography, Space, DatePicker} from "antd";
import { Widget } from "@src/ui/widget";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import {getIconCost, getIconLatency, getIconPipeline, getIconResponse, getIconToken} from "./utils";
import type { Dayjs } from 'dayjs';
import {currencyFormatter, CustomPriceTag, getCurrency} from "@src/components/Common/utils";
import {BaseFilterDropdown} from "@src/components/Filters/BaseFilterDropdown";
import PackDashboardTabs from "../Pack/PackDashboard/PackDashboardTabs";
import MainCard from "@src/components/Common/MainCard";
import FiltersPopupWrapper from "@src/components/Filters/FiltersPopup";
import LoadingVeil from "@src/ui/LoadingVeil";

const { Option } = Select;
const { RangePicker } = DatePicker;


/**
 * What a query last answered, held while it asks again. A new filter is a new
 * query with no data of its own yet, and showing that gap would empty every
 * figure and chart for the moment before refilling them; the old answer stays
 * up under the loading veil instead.
 */
const useSettled = (data: any, loading: boolean) => {
    const last = useRef(data);
    if (!loading) last.current = data;
    return loading ? (data ?? last.current) : data;
};

const AIIntegrationListView = (_props: ListProps) => {
    const currency = getCurrency()

    const resource = "serviceowner_pack_statistics";

    // Filter state management
    const [selectedService, setSelectedService] = useState<number | null>(null);
    const [selectedLlmModel, setSelectedLlmModel] = useState<number | null>(null);
    const [dateRange, setDateRange] = useState<any>(null);
    const [timeInterval, setTimeInterval] = useState<string>("daily");

    type ChartId = 'cost' | 'calls' | 'avgCost' | 'tokens';
    const [activeChart, setActiveChart] = useState<ChartId>('avgCost');
    const [bigChartVisible, setBigChartVisible] = useState(true);

    // The charts are drawn at a pixel height, so the window has to be watched
    // rather than left to CSS: zooming out or opening this on a tall screen
    // should give the plots the room, not the gaps around them.
    const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
    useEffect(() => {
        const onResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    /* The right column is cut to what the left one actually came out as.
       A chart does not always render to the pixel it was asked for — axis
       labels and legends round it — so the only way both columns end on the
       same line is to measure one and fit the other to it. */
    const bigColumnRef = useRef<HTMLDivElement>(null);
    const [bigColumnHeight, setBigColumnHeight] = useState(0);
    useEffect(() => {
        const node = bigColumnRef.current;
        if (!node || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(([entry]) => {
            setBigColumnHeight(Math.round(entry.contentRect.height));
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Dynamic x-axis format based on time interval
    const getXAxisFormat = () => {
        switch (timeInterval) {
            case 'hourly': return 'HH:mm';
            case 'daily': return 'MMM dd';
            case 'weekly': return 'MMM dd';
            case 'monthly': return 'MMM yyyy';
            default: return 'MMM dd';
        }
    };
    const xAxisFormat = getXAxisFormat();

    const swapActiveChart = (next: ChartId) => {
        if (next === activeChart) return;
        // Simple fade/slide animation without extra packages
        setBigChartVisible(false);
        window.setTimeout(() => {
            setActiveChart(next);
            setBigChartVisible(true);
        }, 160);
    };

    // Build filter object for API
    const graphFilter: Record<string, any> = {};
    if (selectedService) graphFilter.service_id = selectedService;
    if (selectedLlmModel) graphFilter.llm_model_id = selectedLlmModel;
    if (dateRange) {
        if (dateRange[0]) {
            graphFilter.start_date = dateRange[0].format('YYYY-MM-DD');
        }
        if (dateRange[1]) {
            graphFilter.end_date = dateRange[1].format('YYYY-MM-DD');
        }
    }
    if (timeInterval) graphFilter.time_interval = timeInterval;

    const { data: graphAnswer, loading: graphLoading } = useQuery({
        type: 'getAction',
        resource: resource,
        payload: { id: null, verb: 'graphs', filter: graphFilter}
    }, {});

    const baseStatisticsEndpoint = 'serviceowner_statistics'
    const { data: avgCostAnswer, loading: avgCostLoading } = useQuery({
        type: 'getAction',
        resource: baseStatisticsEndpoint,
        payload: { id: null, verb: 'pack_cost_average_graph', filter: graphFilter}
    }, {});

    const { data: basicsAnswer, loading: basicsLoading } = useQuery({
        type: 'getAction',
        resource: resource,
        payload: { id: null, verb: 'basics', filter: graphFilter}
    }, {});

    const graphData = useSettled(graphAnswer, graphLoading);
    const AverageCostGraphData = useSettled(avgCostAnswer, avgCostLoading);
    const basicsData = useSettled(basicsAnswer, basicsLoading);

    // Transform graph data for charts
    let chartData = graphData && graphData?.results && graphData?.results.map((item: any) => ({

        date: item[0],
        count: item[1]?.count || 0,
        cost: item[1]?.cost || 0,
        tokens: item[1]?.total_tokens || 0,
    }));
    if(!chartData)
        chartData = []

    // Transform average cost graph data
    let avgCostChartData = AverageCostGraphData && AverageCostGraphData?.results && AverageCostGraphData?.results.map((item: any) => ({
        date: item[0],
        avgCost: item[1]?.aiprs_avg || 0,
    }))
    if(!avgCostChartData)
        avgCostChartData = []

    // Chart Options for Total Spend (Cost)
    const costChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#389e0d'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            categories: chartData.map((d: any) => d.date),
            tickAmount: Math.min(chartData.length, 10),
            labels: {
                datetimeUTC: false,
                format: xAxisFormat,
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => currencyFormatter(currency).format(value || 0),
            }
        },
        tooltip: {
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `$${value.toFixed(2)}`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const costChartSeries = [{
        name: 'Cost',
        data: chartData.map((d: any) => d.cost),
    }];

    // Chart Options for LLM Calls (Count)
    const callsChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#08979c'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            categories: chartData.map((d: any) => d.date),
            tickAmount: Math.min(chartData.length, 10),
            labels: {
                datetimeUTC: false,
                format: xAxisFormat,
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => value.toFixed(0),
            }
        },
        tooltip: {
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `${value} calls`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const callsChartSeries = [{
        name: 'Calls',
        data: chartData.map((d: any) => d.count),
    }];

    // Chart Options for Token Count
    const tokensChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#faad14'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'datetime',
            categories: chartData.map((d: any) => d.date),
            tickAmount: Math.min(chartData.length, 10),
            labels: {
                datetimeUTC: false,
                format: xAxisFormat,
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => `${(value / 1000).toFixed(0)}K`,
            }
        },
        tooltip: {
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `${value.toLocaleString()} tokens`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const tokensChartSeries = [{
        name: 'Tokens',
        data: chartData.map((d: any) => d.tokens),
    }];

    // Chart Options for Average Cost
    const avgCostChartOptions: ApexOptions = {
        chart: {
            type: 'bar',
            height: 280,
            toolbar: { show: false },
        },
        plotOptions: {
            bar: {
                borderRadius: 0,
                horizontal: false,
                columnWidth: '60%',
            }
        },
        dataLabels: { enabled: false },
        colors: ['#1890ff'],
        fill: { type: 'solid' },
        xaxis: {
            type: 'category',
            categories: avgCostChartData.map((d: any) => {
                const date = new Date(d.date);
                if (timeInterval === 'hourly') {
                    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                } else if (timeInterval === 'monthly') {
                    return date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
                }
                return date.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
            }),
            labels: {
                hideOverlappingLabels: true,
                rotate: 0,
            }
        },
        yaxis: {
            labels: {
                formatter: (value) => currencyFormatter(currency).format(value || 0),
            }
        },
        tooltip: {
            enabled: true,
            x: { format: timeInterval === 'hourly' ? 'MMM dd HH:mm' : 'MMM dd, yyyy' },
            y: {
                formatter: (value) => `$${value.toFixed(4)}`,
            }
        },
        grid: {
            borderColor: '#f0f0f0',
        },
    };

    const avgCostChartSeries = [{
        name: 'Average Cost',
        data: avgCostChartData.map((d: any) => d.avgCost),
    }];

    const getChartMeta = (id: ChartId) => {
        switch (id) {
            case 'cost':
                return { title: 'Token Costs', icon: getIconCost(), type: 'bar' as const, options: costChartOptions, series: costChartSeries };
            case 'calls':
                return { title: 'LLM Calls', icon: getIconResponse(), type: 'bar' as const, options: callsChartOptions, series: callsChartSeries };
            case 'avgCost':
                return { title: 'Average Cost per Change Instance', icon: getIconCost(), type: 'bar' as const, options: avgCostChartOptions, series: avgCostChartSeries };
            case 'tokens':
                return { title: 'Token Count', icon: getIconToken(), type: 'bar' as const, options: tokensChartOptions, series: tokensChartSeries };
        }
    };

    const renderChart = (id: ChartId, size: 'big' | 'mini') => {
        const meta = getChartMeta(id);
        const isBig = size === 'big';
        const isActive = id === activeChart;
        // the average cost comes from an endpoint of its own
        const chartLoading = id === 'avgCost' ? avgCostLoading : graphLoading;
        const chartAnswered = !!(id === 'avgCost' ? AverageCostGraphData : graphData);
        /* Both columns are exactly COLUMN_H tall, whatever the window is.
           Left:  header + border + padding + plot
           Right: three of (header + border + plot), with two gaps between them
           Every one of those parts is fixed here or set on the markup below, so
           the two add up to the same number rather than to an estimate. */
        const HEADER_H = 28;        // fixed on the header row of both sizes
        const BORDER_H = 2;         // the plot wrapper's 1px, top and bottom
        const BIG_PAD_H = 16;       // the big wrapper's 8px padding, top and bottom
        const MINI_GAP = 8;         // the Space between the stacked minis
        // Sized to the window: a fixed height suits exactly one screen, and on a
        // tall one it leaves the charts short with the page empty around them.
        const columnHeight = Math.max(500, Math.min(820, Math.round(viewportHeight * 0.55)));

        const bigHeight = columnHeight - HEADER_H - BORDER_H - BIG_PAD_H;
        // measured once the big chart is on the page; the formula only has to
        // hold for the first paint
        const target = bigColumnHeight || columnHeight;
        const miniHeight = Math.max(80, Math.floor(
            (target - (3 * (HEADER_H + BORDER_H)) - (2 * MINI_GAP)) / 3,
        ));
        const height = isBig ? bigHeight : miniHeight;

        const miniOptions: ApexOptions = isBig
            ? {
                ...meta.options,
                chart: { ...(meta.options.chart || {}), height },
            }
            : {
                ...meta.options,
                chart: { ...(meta.options.chart || {}), height, toolbar: { show: false } },
                // Show labels on axes for minor charts (requested)
                xaxis: { ...(meta.options.xaxis || {}), labels: { ...((meta.options.xaxis as any)?.labels || {}), show: true } },
                yaxis: { ...(meta.options.yaxis || {}), labels: { ...((meta.options.yaxis as any)?.labels || {}), show: true } },
                grid: { ...(meta.options.grid || {}), padding: { left: 6, right: 6, top: 0, bottom: 0 } as any },
                tooltip: meta.options.tooltip,
            };

        // Big chart container animation
        const animatedStyle: React.CSSProperties = isBig
            ? {
                opacity: bigChartVisible ? 1 : 0,
                transform: bigChartVisible ? 'translateY(0px)' : 'translateY(8px)',
                transition: 'opacity var(--netorca-dur-slow) var(--netorca-ease-standard), transform var(--netorca-dur-slow) var(--netorca-ease-standard)',
            }
            : {};

        // Always show the interval selector on the big chart header
        const headerRight = isBig ? (
            <Select
                size="small"
                value={timeInterval}
                onChange={(value) => setTimeInterval(value)}
                style={{ width: 110 }}
                className="filter-select"
            >
                <Option value="hourly">Hourly</Option>
                <Option value="daily">Daily</Option>
                <Option value="weekly">Weekly</Option>
                <Option value="monthly">Monthly</Option>
            </Select>
        ) : null;

        const wrapperStyle: React.CSSProperties = {
            background: 'hsl(0 0% 100%)',
            borderRadius: '8px',
            // remove inner whitespace for mini charts
            padding: isBig ? '8px' : '0px',
            border: isBig ? '1px solid hsl(0 0% 89.8%)' : (isActive ? '1px solid #1890ff' : '1px solid hsl(0 0% 89.8%)'),
            cursor: isBig ? 'default' : 'pointer',
            boxShadow: isBig ? 'none' : (isActive ? '0 0 0 2px rgba(24, 144, 255, 0.15)' : 'none'),
            transition: 'box-shadow var(--netorca-dur-mid) var(--netorca-ease-standard), border-color var(--netorca-dur-mid) var(--netorca-ease-standard), transform var(--netorca-dur-mid) var(--netorca-ease-standard)',
        };

        return (
            <div
                role={isBig ? undefined : 'button'}
                aria-label={isBig ? undefined : `Show ${meta.title}`}
                title={isBig ? undefined : 'Click to zoom'}
                onClick={isBig ? undefined : () => swapActiveChart(id)}
                style={{ width: '100%', userSelect: 'none' }}
            >
                <div
                    style={{
                        height: `${HEADER_H}px`,
                        boxSizing: 'border-box',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Space align="center" size={4}>
                        <span>{meta.icon}</span>
                        <h3
                            style={{
                                fontSize: isBig ? '16px' : '14px',
                                fontWeight: 600,
                                margin: 0,
                                color: 'hsl(220 40% 25%)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {meta.title}
                        </h3>
                    </Space>
                    {headerRight}
                </div>

                <div style={{ ...wrapperStyle, ...(isBig ? animatedStyle : {}) }}>
                    {/* The plot box is drawn at its height whether or not there
                        is anything in it yet, so loading never resizes it. Before
                        the first answer it is empty under the veil — not "No
                        data available", which is a claim about the answer. */}
                    <LoadingVeil loading={!!chartLoading} label={`Loading ${meta.title}`}>
                        {((id === 'avgCost' ? avgCostChartData.length : chartData.length) > 0) ? (
                            <ReactApexChart
                                key={isBig ? `big-${activeChart}` : `mini-${id}`}
                                options={miniOptions}
                                series={meta.series}
                                type={meta.type}
                                height={height}
                            />
                        ) : (
                            <div style={{ height: `${height}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {!chartAnswered ? null : (
                                    <Typography.Text type="secondary">No data available</Typography.Text>
                                )}
                            </div>
                        )}
                    </LoadingVeil>
                </div>
            </div>
        );
    };

    return (
        // no card of its own: the page's frame is the card, and a second one
        // inside it only draws a box around the box
        <div className="ai-analytics">
            {/* Filters and figures. No flex growth here: this block is as tall as
                what is in it, or it takes the height the charts should have and
                strands them at the bottom of the page. */}
            <div style={{ flex: "0 0 auto", overflowX: "hidden" }}>
                    <Space orientation="vertical" size={10} style={{ width: "100%", display: "flex"}}>
                            {/* Widgets, then the filter button closing the row */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', flexWrap: 'wrap' }}>
                                <FiltersPopupWrapper>
                                    <div className="ai-analytics__filters">
                                        <BaseFilterDropdown
                                            // title="Service"
                                            allTitle="All Services"
                                            placeholder="Select service"
                                            value={selectedService}
                                            onChange={(value) => setSelectedService(value)}
                                            resource="services"
                                        />
                                        <BaseFilterDropdown
                                            // title="LLM Model"
                                            allTitle="All Models"
                                            placeholder="Select LLM model"
                                            value={selectedLlmModel}
                                            onChange={(value) => setSelectedLlmModel(value)}
                                            resource="llm_models"
                                        />
                                        <div style={{ flex: '0 0 auto', minWidth: '200px' }}>
                                            <label className="filter-label">
                                                Date Range
                                            </label>
                                            <RangePicker
                                                style={{ width: "400px", borderRadius: "8px" }}
                                                value={dateRange}
                                                onChange={(dates) => setDateRange(dates)}
                                            />
                                        </div>
                                    </div>
                                </FiltersPopupWrapper>

                                <Row gutter={[6, 12]} style={{ flex: 1, order: -1 }}>
                                    <Col xs={12} sm={12} md={5}>
                                        <Widget
                                            title="Total Pipelines"
                                            value={basicsData ? basicsData.total_pipelines || 0 : '—'}
                                            icon={getIconPipeline()}
                                            status="info"
                                            loading={basicsLoading}
                                            inline
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <Widget
                                            title="Total Cost"
                                            value={basicsData ? CustomPriceTag(basicsData.total_cost || 0) : '—'}
                                            icon={getIconCost()}
                                            status="success"
                                            loading={basicsLoading}
                                            inline
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={6}>
                                        <Widget
                                            title="Total LLM calls"
                                            value={basicsData ? basicsData.total_llm_calls || 0 : '—'}
                                            icon={getIconResponse()}
                                            status="default"
                                            loading={basicsLoading}
                                            inline
                                        />
                                    </Col>
                                    <Col xs={12} sm={12} md={7}>
                                        <Widget
                                            title="Average Response Time"
                                            value={!basicsData ? '—' : basicsData.avg_latency_ms ? (basicsData.avg_latency_ms / 1000).toFixed(2) : 0}
                                            valueSuffix={basicsData ? 's' : ''}
                                            icon={getIconLatency()}
                                            status="default"
                                            loading={basicsLoading}
                                            inline
                                        />
                                    </Col>
                                </Row>
                            </div>

                        </Space>
            </div>

            {/* Graph Section - 1 Big + 3 Small (click to swap) */}
            <Row style={{ width: "100%", flex: "1 1 auto" }}>
                {/* no box: the page's frame is the only one */}
                <div style={{ width: "100%", padding: "10px 0" }}>
                    <Row gutter={[8, 8]} align="top">
                        {/* Big Chart — measured, so the column beside it can end
                            on the same line */}
                        <Col xs={24} lg={17}>
                            <div ref={bigColumnRef}>
                                {renderChart(activeChart, 'big')}
                            </div>
                        </Col>

                        {/* Minor Charts (right side) */}
                        <Col xs={24} lg={7}>
                            <Space orientation="vertical" size={8} style={{ width: '100%' }}>
                                {(['cost', 'calls', 'avgCost', 'tokens'] as ChartId[])
                                    .filter((id) => id !== activeChart)
                                    .slice(0, 3)
                                    .map((id) => (
                                        <div
                                            key={id}
                                            style={{
                                                width: '100%',
                                                transform: 'translateY(0px)',
                                            }}
                                            onMouseEnter={(e) => {
                                                // subtle hover lift without extra packages
                                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0px)';
                                            }}
                                        >
                                            {renderChart(id, 'mini')}
                                        </div>
                                    ))}
                            </Space>
                        </Col>
                    </Row>
                </div>
            </Row>

        </div>
    );
};

const AIIntegrationList = (props: ListProps) => {
    return (
        // one frame for the page: the tabs and what they lead to, together
        <div className="netorca-ai netorca-frame">
            <MainCard>
                <PackDashboardTabs />
                <AIIntegrationListView />
            </MainCard>
        </div>
    )
}

export default AIIntegrationList;
